import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { controlRoomAPI, accountsAPI } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ssoUnavailable, setSsoUnavailable] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = sessionStorage.getItem('token');
    const savedUser = sessionStorage.getItem('user');
    
    if (token && savedUser) {
      try {
        const response = await accountsAPI.getMe();
        const userData = JSON.parse(savedUser);
        setUser({ ...userData, ...response.data });
      } catch (e) {
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
      }
    }
    setLoading(false);
  };

  const login = useCallback(async (email, password) => {
    try {
      setError(null);
      setSsoUnavailable(false);
      
      const response = await controlRoomAPI.login(email, password, 'ACCOUNTS');
      const { 
        access_token, 
        user_id, 
        user_name, 
        user_email, 
        org_id, 
        org_name,
        current_outlet_id,
        is_org_admin,
        allowed_apps
      } = response.data;
      
      const userData = {
        user_id,
        name: user_name,
        email: user_email,
        org_id,
        org_name,
        outlet_id: current_outlet_id,
        is_org_admin,
        allowed_apps
      };
      
      sessionStorage.setItem('token', access_token);
      sessionStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      
      return { success: true };
    } catch (err) {
      if (err.code === 'ERR_NETWORK' || err.message?.includes('Network Error')) {
        setSsoUnavailable(true);
        return { success: false, error: 'Control Room SSO service is unavailable. Please try again later.' };
      }
      
      const message = err.response?.data?.detail || 'Login failed';
      setError(message);
      return { success: false, error: message };
    }
  }, []);

  const logout = useCallback(() => {
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    setUser(null);
    setSsoUnavailable(false);
  }, []);

  const isAuthenticated = !!user;
  
  const hasRole = useCallback((roles) => {
    if (!user) return false;
    if (user.is_org_admin || user.is_admin) return true;
    return false;
  }, [user]);

  const canWrite = useCallback(() => user?.is_org_admin || user?.is_admin || hasRole(['OWNER', 'FINANCE']), [user, hasRole]);
  const canExport = useCallback(() => user?.is_org_admin || user?.is_admin || hasRole(['OWNER', 'FINANCE', 'CA']), [user, hasRole]);
  const isReadOnly = useCallback(() => hasRole(['AUDITOR', 'VIEWER']), [hasRole]);

  const value = useMemo(() => ({
    user,
    loading,
    error,
    ssoUnavailable,
    login,
    logout,
    isAuthenticated,
    hasRole,
    canWrite,
    canExport,
    isReadOnly,
  }), [user, loading, error, ssoUnavailable, login, logout, isAuthenticated, hasRole, canWrite, canExport, isReadOnly]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
